import { useState, useEffect, useMemo } from 'react';
import { useFactory } from '../../../../context/FactoryContext';
import { employeeService } from '../services/employeeService';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [5, 10, 25];

export const useWorkday = (onSyncSuccess) => {
  const [workdayEmployees, setWorkdayEmployees] = useState([]);
  const [workdayLoading, setWorkdayLoading] = useState(false);
  const [workdayError, setWorkdayError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [workdayPage, setWorkdayPage] = useState(1);
  const [workdayPageSize, setWorkdayPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedWorkdayEmployee, setSelectedWorkdayEmployee] = useState(null);
  const [showWorkdayDetailModal, setShowWorkdayDetailModal] = useState(false);
  const [workdayDetailLoading, setWorkdayDetailLoading] = useState(false);
  const { selectedFactory: selectedFactoryFromContext } = useFactory();
  const factory = selectedFactoryFromContext || '';

  // Función para cargar datos de Workday
  const loadWorkdayEmployees = async () => {
    try {
      setWorkdayLoading(true);
      setWorkdayError(null);
      const employees = await employeeService.getWorkdayEmployees(factory);
      setWorkdayEmployees(employees);
    } catch (err) {
      console.error('Error cargando empleados de Workday:', err);
      const errorMessage = err?.message || err?.toString() || 'Error al cargar los empleados de Workday';
      setWorkdayError(errorMessage);
      setWorkdayEmployees([]);
    } finally {
      setWorkdayLoading(false);
    }
  };

  // Función de sincronización de Workday
  const handleSyncWorkday = async () => {
    try {
      setSyncing(true);
      setWorkdayError(null);
      await employeeService.syncWorkday();
      if (onSyncSuccess) {
        await onSyncSuccess();
      }
      alert('Sincronización completada exitosamente');
    } catch (err) {
      console.error('Error sincronizando empleados de Workday:', err);
      setWorkdayError(err.message || 'Error al sincronizar los empleados de Workday');
      alert(err.message || 'Error al sincronizar los empleados de Workday');
    } finally {
      setSyncing(false);
    }
  };

  // Función para procesar ficheros JSON de Workday en el directorio configurado
  const handleProcessWorkday = async () => {
    try {
      setSyncing(true);
      setWorkdayError(null);
      const response = await employeeService.processWorkdayFiles();
      const message = response?.message || 'Procesamiento de ficheros completado exitosamente';
      await loadWorkdayEmployees();
      alert(message);
    } catch (err) {
      console.error('Error procesando ficheros de Workday:', err);
      const errorMessage = err?.message || 'Error al procesar los ficheros de Workday';
      setWorkdayError(errorMessage);
      alert(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  // Función para cargar detalles de un empleado de Workday
  const loadWorkdayEmployeeDetails = async (wid) => {
    if (!wid) return;
    try {
      setWorkdayDetailLoading(true);
      const employee = await employeeService.getWorkdayEmployeeDetails(wid);
      if (employee) {
        setSelectedWorkdayEmployee(employee);
        setShowWorkdayDetailModal(true);
      } else {
        alert('No se encontraron detalles del empleado');
      }
    } catch (err) {
      console.error('Error cargando detalles del empleado de Workday:', err);
      alert(err?.message || 'Error al cargar los detalles del empleado');
    } finally {
      setWorkdayDetailLoading(false);
    }
  };

  // Cargar datos de Workday al montar y cuando cambie la fábrica (desde el contexto)
  useEffect(() => {
    try {
      loadWorkdayEmployees();
    } catch (err) {
      console.error('Error en useEffect de Workday:', err);
      setWorkdayError('Error al inicializar la carga de empleados de Workday');
    }
  }, [factory]);

  // Paginación para Workday
  const workdayTotalItems = workdayEmployees.length;
  const workdayTotalPages = Math.max(1, Math.ceil(workdayTotalItems / workdayPageSize));
  const workdayEmployeesToShow = useMemo(
    () => workdayEmployees.slice((workdayPage - 1) * workdayPageSize, workdayPage * workdayPageSize),
    [workdayEmployees, workdayPage, workdayPageSize]
  );
  const workdayRangeStart = workdayTotalItems === 0 ? 0 : (workdayPage - 1) * workdayPageSize + 1;
  const workdayRangeEnd = Math.min(workdayPage * workdayPageSize, workdayTotalItems);

  // Reset página Workday cuando cambie la lista o el tamaño de página
  useEffect(() => {
    setWorkdayPage(1);
  }, [workdayTotalItems, workdayPageSize]);

  return {
    workdayEmployees,
    workdayLoading,
    workdayError,
    syncing,
    workdayPage,
    setWorkdayPage,
    workdayPageSize,
    setWorkdayPageSize,
    workdayPageSizeOptions: PAGE_SIZE_OPTIONS,
    selectedWorkdayEmployee,
    showWorkdayDetailModal,
    setShowWorkdayDetailModal,
    workdayDetailLoading,
    workdayEmployeesToShow,
    workdayTotalItems,
    workdayTotalPages,
    workdayRangeStart,
    workdayRangeEnd,
    handleSyncWorkday,
    handleProcessWorkday,
    loadWorkdayEmployeeDetails
  };
};